import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/features/auth/session';
import { useAcceptOrganizationInvitation } from '@/features/membership/hooks';
import { ROLE_LABELS } from '@/features/membership/labels';
import {
  clearPendingInvitationToken,
  getPendingInvitationToken,
  setPendingInvitationToken,
} from '@/features/membership/pending-token';
import { useActiveOrganization } from '@/features/organization/active-organization-context';
import { ApiError } from '@/lib/api/client';

const GENERIC_INVALID_MESSAGE =
  'Tautan undangan tidak valid, sudah digunakan, sudah dibatalkan, atau tidak sesuai dengan akun yang sedang masuk.';

/**
 * The organization invitation acceptance route (ADR-027, Decision 13:
 * authentication alone gates redemption - never a prior organization
 * membership, since acceptance is what may create one). Shared across all
 * three invitable roles (Organization Admin, Teacher, Student) - the only
 * thing that differs per role is the label shown after a successful
 * acceptance; there is no role-specific input to collect here, since
 * every field ADR-027 requires (including a Student's optional guardian
 * contact) is supplied by the inviter at invite time, not by the
 * recipient at acceptance (ADR-027, Decisions 4-5). This mirrors
 * `features/guardian/accept-invitation-page.tsx` exactly - the same
 * mechanics, a second dedicated token store (`pending-token.ts`), never a
 * second token *system*.
 *
 * Captures `?token=` on first load, stores it under its own sessionStorage
 * key so it survives a login/register redirect, and immediately scrubs it
 * from the URL/history.
 */
export function AcceptInvitationPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { status, logout, refreshUser } = useAuth();
  const { select } = useActiveOrganization();
  const accept = useAcceptOrganizationInvitation();
  const attempted = useRef(false);
  const [missingToken, setMissingToken] = useState(false);

  useEffect(() => {
    const urlToken = searchParams.get('token');
    if (urlToken !== null && urlToken.length > 0) {
      setPendingInvitationToken(urlToken);
      void navigate('/organization-invitations/accept', { replace: true });
      return;
    }

    if (getPendingInvitationToken() === null) {
      setMissingToken(true);
    }
    // Only runs once per mount: the URL token, once captured, is
    // immediately scrubbed above, so this effect must not re-fire on the
    // resulting navigation and treat the now-clean URL as "missing".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status !== 'authenticated' || attempted.current) {
      return;
    }
    const token = getPendingInvitationToken();
    if (token === null) {
      return;
    }
    attempted.current = true;
    accept.mutate(token, {
      onSuccess: (result) => {
        clearPendingInvitationToken();
        // Re-resolve membership truthfully via /auth/me first (ADR-026
        // Addendum, AO-13) - only once the refreshed membership list
        // actually contains the newly-granted organization does `select`
        // accept it (AO-15: never trust an id that isn't a current
        // membership). ADR-027, Decision 13: "once acceptance succeeds,
        // normal Active Organization rules resume unchanged" - this is
        // exactly those unchanged rules, reused verbatim from
        // `accept-invitation-page.tsx`'s own Guardian flow, not a new
        // auto-select behavior invented for this module. Best-effort: a
        // transient refresh failure must not undo a successful
        // acceptance.
        refreshUser()
          .then(() => {
            select(result.organizationId);
          })
          .catch(() => {
            // Intentionally ignored - see comment above.
          });
      },
      // Deliberately not cleared on error - see the module doc comment:
      // the backend collapses invalid/used/cancelled and email-mismatch
      // into one generic code, so an email mismatch (recoverable by
      // logging into the correct account) must not lose the still-valid
      // token.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (missingToken) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Tautan Tidak Ditemukan</CardTitle>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>{GENERIC_INVALID_MESSAGE}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Skeleton className="h-8 w-48" />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Masuk untuk Melanjutkan</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              Masuk atau daftar dengan email yang menerima undangan untuk melanjutkan.
            </p>
            <div className="flex gap-2">
              <Button asChild>
                <Link to="/login" state={{ from: '/organization-invitations/accept' }}>
                  Masuk
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/register" state={{ from: '/organization-invitations/accept' }}>
                  Daftar
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accept.isSuccess) {
    const roleLabel = ROLE_LABELS[accept.data.targetRoleName];
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Undangan Diterima</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Alert>
              <AlertTitle>Berhasil</AlertTitle>
              <AlertDescription>Anda kini bergabung sebagai {roleLabel}.</AlertDescription>
            </Alert>
            <Button
              onClick={() => {
                void navigate('/');
              }}
            >
              Lanjutkan
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accept.isError) {
    const message =
      accept.error instanceof ApiError && accept.error.code === 'ORGANIZATION_INVITATION_INVALID'
        ? GENERIC_INVALID_MESSAGE
        : 'Terjadi kesalahan. Silakan coba lagi.';

    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Tidak Dapat Menerima Undangan</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Alert variant="destructive">
              <AlertDescription>{message}</AlertDescription>
            </Alert>
            <p className="text-sm text-muted-foreground">
              Jika undangan ini ditujukan untuk akun lain, keluar dan masuk dengan akun yang sesuai.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                void logout();
              }}
            >
              Keluar dan Gunakan Akun Lain
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4" role="status">
      <Skeleton className="h-8 w-48" />
    </div>
  );
}
